/**
 * INVOICE PDF
 * Component for generating PDF invoices
 * Note: This is a simplified version. For production, use react-pdf or jsPDF
 */

import React from 'react';

export default function InvoicePDF({ invoice, lineItems = [] }) {
  const handleDownload = () => {
    // For now, open print dialog
    // In production, use jsPDF or react-pdf for proper PDF generation
    window.print();
  };

  if (!invoice) return null;

  return (
    <div>
      {/* Print Button */}
      <button
        onClick={handleDownload}
        className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 print:hidden"
      >
        Download / Print PDF
      </button>

      {/* Invoice Content (printable) */}
      <div className="bg-white p-8 rounded-lg shadow print:shadow-none max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b-2 border-gray-300 pb-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">INVOICE</h1>
              <p className="text-sm text-gray-600 mt-2">#{invoice.invoice_number}</p>
            </div>
            <div className="text-right">
              <h2 className="text-xl font-semibold text-gray-900">SurFox</h2>
              <p className="text-sm text-gray-600">Financial Intelligence System</p>
              <p className="text-sm text-gray-600">hello@surfox.ai</p>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Bill To:</h3>
            <p className="text-sm text-gray-900 font-medium">{invoice.customer_name || 'Customer'}</p>
            <p className="text-sm text-gray-600">{invoice.tenant_id}</p>
          </div>
          <div className="text-right">
            <div className="space-y-1">
              <div className="flex justify-end gap-4">
                <span className="text-sm font-medium text-gray-700">Invoice Date:</span>
                <span className="text-sm text-gray-900">{new Date(invoice.invoice_date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-end gap-4">
                <span className="text-sm font-medium text-gray-700">Due Date:</span>
                <span className="text-sm text-gray-900">{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="flex justify-end gap-4">
                <span className="text-sm font-medium text-gray-700">Period:</span>
                <span className="text-sm text-gray-900">
                  {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <table className="w-full mb-8">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700">Description</th>
              <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700">Quantity</th>
              <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700">Unit Price</th>
              <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700">Total</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, idx) => (
              <tr key={idx} className="border-b border-gray-200">
                <td className="py-3 px-2 text-sm text-gray-900">{item.description}</td>
                <td className="py-3 px-2 text-sm text-gray-900 text-right">{parseFloat(item.quantity).toFixed(2)}</td>
                <td className="py-3 px-2 text-sm text-gray-900 text-right">${parseFloat(item.unit_price).toFixed(4)}</td>
                <td className="py-3 px-2 text-sm text-gray-900 text-right font-medium">${parseFloat(item.total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-64 space-y-2">
            <div className="flex justify-between py-2">
              <span className="text-sm font-medium text-gray-700">Subtotal:</span>
              <span className="text-sm text-gray-900">${parseFloat(invoice.subtotal).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm font-medium text-gray-700">Tax:</span>
              <span className="text-sm text-gray-900">${parseFloat(invoice.tax || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 border-t-2 border-gray-300">
              <span className="text-lg font-bold text-gray-900">Total:</span>
              <span className="text-lg font-bold text-gray-900">${parseFloat(invoice.total).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="border-t pt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes:</h3>
            <p className="text-sm text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="border-t pt-6 mt-8 text-center text-xs text-gray-500">
          <p>Thank you for your business!</p>
          <p className="mt-2">For questions about this invoice, contact hello@surfox.ai</p>
        </div>
      </div>
    </div>
  );
}
